import { watch } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import App from '../App.vue'
import LoginPage from '../components/LoginPage.vue'
import { MOBILE_BREAKPOINT } from '../composables/useViewport'
import { useTenantBranding } from '../composables/useTenantBranding'
import { findNavView } from '../nav/adminNavItems.js'
import { useDemoMode } from '../composables/useDemoMode'

const TeacherAvailabilityPage = () => import('../components/TeacherAvailabilityPage.vue')
const HardshipAcceptPage  = () => import('../components/HardshipAcceptPage.vue')
const CommunicationPreferencesPage = () => import('../components/CommunicationPreferencesPage.vue')
const AdminLayout = () => import('../layouts/AdminLayout.vue')
const OperationsView = () => import('../components/admin/views/OperationsView.vue')
const SubmissionsView = () => import('../components/admin/views/SubmissionsView.vue')
const ContactsView = () => import('../components/admin/views/ContactsView.vue')
const DirectoryView = () => import('../components/admin/views/DirectoryView.vue')
const EmailsView = () => import('../components/admin/views/EmailsView.vue')
const EmailLogTab = () => import('../components/admin/views/EmailLogTab.vue')
const SmsLogTab = () => import('../components/admin/views/SmsLogTab.vue')
const MessageLogsView = () => import('../components/admin/views/MessageLogsView.vue')
const SmsInboxTab = () => import('../components/admin/views/SmsInboxTab.vue')
const AnnouncementTab = () => import('../components/admin/views/AnnouncementTab.vue')
const EmailPreviewTab = () => import('../components/admin/views/EmailPreviewTab.vue')
const InboxTab = () => import('../components/admin/views/InboxTab.vue')
const WidgetFunnelView = () => import('../components/admin/views/WidgetFunnelView.vue')
const TeachersView = () => import('../components/admin/views/TeachersView.vue')
const CalendarView = () => import('../components/admin/views/CalendarView.vue')
const CalendarListView = () => import('../components/admin/views/CalendarListView.vue')
const DayView = () => import('../components/admin/views/DayView.vue')
const WeekView = () => import('../components/admin/views/WeekView.vue')
const MonthView = () => import('../components/admin/views/MonthView.vue')
const FinancialView = () => import('../components/admin/views/FinancialView.vue')
const BillingRunsView = () => import('../components/admin/views/BillingRunsView.vue')
const RecitalView = () => import('../components/admin/views/RecitalView.vue')
const PayrollView = () => import('../components/admin/views/PayrollView.vue')
const PaymentsView = () => import('../components/admin/views/PaymentsView.vue')
const PricingView = () => import('../components/admin/views/PricingView.vue')
const FinancialDiscountsTab = () => import('../components/admin/views/FinancialDiscountsTab.vue')
const W2ReportingView = () => import('../components/admin/views/W2ReportingView.vue')
const OnboardingSpecialistView = () => import('../components/admin/views/OnboardingSpecialistView.vue')
const DbBrowserView = () => import('../components/admin/views/DbBrowserView.vue')
const MembershipsView = () => import('../components/admin/views/MembershipsView.vue')
const LessonSeriesView = () => import('../components/admin/views/LessonSeriesView.vue')
const BankingTab = () => import('../components/admin/views/BankingTab.vue')
const FinancialAnalyticsTab = () => import('../components/admin/views/FinancialAnalyticsTab.vue')
const StudentsView = () => import('../components/admin/views/StudentsView.vue')
const RoomsView = () => import('../components/admin/views/RoomsView.vue')
const BusinessProfileView = () => import('../components/admin/views/BusinessProfileView.vue')
const BusinessProfileTab = () => import('../components/admin/views/BusinessProfileTab.vue')
const DnsSetupView = () => import('../components/admin/views/DnsSetupView.vue')
const WidgetsView = () => import('../components/admin/views/WidgetsView.vue')
const NotificationPreferencesView = () => import('../components/admin/views/NotificationPreferencesView.vue')
const LessonPackageTiersView = () => import('../components/admin/views/LessonPackageTiersView.vue')
const MembershipOffersTab = () => import('../components/admin/views/MembershipOffersTab.vue')
const HardshipOfferTab = () => import('../components/admin/views/HardshipOfferTab.vue')
const StripeConnectTab = () => import('../components/admin/views/StripeConnectTab.vue')
const SiteAnalyticsSettingsTab = () => import('../components/admin/views/SiteAnalyticsSettingsTab.vue')
const AgreementPoliciesTab = () => import('../components/admin/views/AgreementPoliciesTab.vue')
const SiteAnalyticsView = () => import('../components/admin/views/SiteAnalyticsView.vue')

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    name: 'Dashboard',
    component: App,
    // AttendanceTab already auto-scrolls to the next lesson / "now" marker on
    // load (see AttendanceTab.vue's scrollToFocusTarget) — generic restore
    // would fight that.
    meta: { requiresAuth: true, skipScrollRestore: true }
  },
  {
    path: '/signups',
    name: 'DashboardSignups',
    component: App,
    meta: { requiresAuth: true }
  },
  {
    path: '/stats',
    name: 'DashboardStats',
    component: App,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin',
    component: AdminLayout,
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: '', redirect: () => localStorage.getItem('adminLastView') || '/admin/calendar' },
      { path: 'operations',    name: 'AdminOperations',    component: OperationsView,    meta: { title: 'Operations', requiresBma: true } },
      { path: 'submissions',   name: 'AdminSubmissions',   component: SubmissionsView,   meta: { title: 'Submissions' } },
      {
        path: 'contacts',
        name: 'AdminContacts',
        component: ContactsView,
        children: [
          { path: '',          redirect: '/admin/contacts/directory' },
          { path: 'directory', name: 'AdminDirectory', component: DirectoryView, meta: { title: 'Directory' } },
          { path: 'students',  name: 'AdminStudents',  component: StudentsView,  meta: { title: 'Students' } },
          { path: 'teachers',  name: 'AdminTeachers',  component: TeachersView,  meta: { title: 'Teachers' } },
        ]
      },
      { path: 'students',      redirect: '/admin/contacts/students' },
      {
        path: 'messages',
        name: 'AdminEmails',
        component: EmailsView,
        children: [
          { path: '',          redirect: '/admin/messages/broadcast' },
          { path: 'broadcast', name: 'AdminEmailsCompose', component: AnnouncementTab, meta: { title: 'Broadcast' } },
          {
            path: 'logs',
            component: MessageLogsView,
            children: [
              { path: '',      redirect: '/admin/messages/logs/email' },
              { path: 'email', name: 'AdminEmailsLog', component: EmailLogTab, meta: { title: 'Email Log' } },
              { path: 'texts', name: 'AdminSmsLog',    component: SmsLogTab,   meta: { title: 'Text Log' } },
            ]
          },
          // Pre-2026-09-26 separate log pages — kept for bookmarks.
          { path: 'email-log', redirect: '/admin/messages/logs/email' },
          { path: 'text-log',  redirect: '/admin/messages/logs/texts' },
          { path: 'texts',     name: 'AdminSmsInbox',      component: SmsInboxTab,     meta: { title: 'Texts' } },
          { path: 'inbox',     name: 'AdminEmailsInbox',   component: InboxTab,        meta: { title: 'Email', requiresBma: true } },
        ]
      },
      // Pre-2026-09-23 paths (Emails + SMS nav groups) — kept for bookmarks.
      { path: 'emails',           redirect: '/admin/messages/broadcast' },
      { path: 'emails/compose',   redirect: '/admin/messages/broadcast' },
      { path: 'emails/log',       redirect: '/admin/messages/logs/email' },
      { path: 'emails/sms-log',   redirect: '/admin/messages/logs/texts' },
      { path: 'emails/sms-inbox', redirect: '/admin/messages/texts' },
      { path: 'emails/inbox',     redirect: '/admin/messages/inbox' },
      { path: 'announcements',    redirect: '/admin/messages/broadcast' },
      { path: 'email-preview', name: 'AdminEmailPreview', component: EmailPreviewTab, meta: { title: 'Email Preview', requiresBma: true } },
      { path: 'widget-funnel', name: 'AdminWidgetFunnel', component: WidgetFunnelView, meta: { title: 'Widget Funnel' } },
      { path: 'teachers',      redirect: '/admin/contacts/teachers' },
      {
        path: 'calendar',
        name: 'AdminCalendar',
        component: CalendarView,
        children: [
          { path: '',      redirect: '/admin/calendar/day' },
          { path: 'list',  name: 'AdminCalendarList', component: CalendarListView, meta: { title: 'Lessons' } },
          // DayView/WeekView auto-scroll to the relevant hour on load (scrollToRelevantTime)
          // — generic restore would fight that, same reasoning as the Dashboard route above.
          // DayView is shown as "Week"; the real WeekView is disabled (not deleted) — to
          // re-enable, restore the AdminWeek route below and its entries in calendarTabs.js,
          // useSearchableViews.js and App.vue's TEAM_CALENDAR_MODES.
          { path: 'day',   name: 'AdminDay',          component: DayView,         meta: { title: 'Week View', skipScrollRestore: true } },
          { path: 'week',  redirect: '/admin/calendar/day' },
          // { path: 'week',  name: 'AdminWeek',         component: WeekView,        meta: { title: 'Week View', skipScrollRestore: true } },
          { path: 'month', name: 'AdminMonth',        component: MonthView,       meta: { title: 'Month View' } },
        ]
      },
      { path: 'week', redirect: '/admin/calendar/day' },
      { path: 'financial',     name: 'AdminFinancial',     component: FinancialView,          meta: { title: 'Billing' } },
      { path: 'billing-runs', name: 'AdminBillingRuns', component: BillingRunsView, meta: { title: 'Billing Runs' } },
      { path: 'recital',      name: 'AdminRecital',     component: RecitalView,     meta: { title: 'Recital' } },
      { path: 'upcoming-charges', redirect: '/admin/billing-runs' },
      { path: 'payments',      name: 'AdminPayments',      component: PaymentsView,           meta: { title: 'Payments' } },
      {
        path: 'pricing',
        name: 'AdminPricing',
        component: PricingView,
        children: [
          { path: '',                  redirect: '/admin/pricing/discounts' },
          { path: 'discounts',         name: 'AdminPricingDiscounts',        component: FinancialDiscountsTab,  meta: { title: 'Discounts' } },
          { path: 'lesson-packages',   name: 'AdminPricingLessonPackages',   component: LessonPackageTiersView, meta: { title: 'Lesson Packages' } },
          { path: 'membership-offers', name: 'AdminPricingMembershipOffers', component: MembershipOffersTab,    meta: { title: 'Membership Offers' } },
          { path: 'hardship-offer',    name: 'AdminPricingHardshipOffer',    component: HardshipOfferTab,       meta: { title: 'Hardship Offer' } },
        ]
      },
      { path: 'payroll',       name: 'AdminPayroll',       component: PayrollView,            meta: { title: 'Payroll' } },
      { path: 'w2-reporting',  name: 'AdminW2Reporting',   component: W2ReportingView,        meta: { title: 'W-2 Reporting' } },
      { path: 'onboarding-specialist', name: 'AdminOnboardingSpecialist', component: OnboardingSpecialistView, meta: { title: 'Onboarding Specialist', requiresBma: true } },
      { path: 'db-browser',    name: 'AdminDbBrowser',    component: DbBrowserView,          meta: { title: 'DB Browser', requiresBma: true } },
      { path: 'memberships',   name: 'AdminMemberships',   component: MembershipsView,        meta: { title: 'Memberships' } },
      { path: 'lesson-series', name: 'AdminLessonSeries',  component: LessonSeriesView,       meta: { title: 'Lesson Series' } },
      { path: 'banking',       name: 'AdminBanking',       component: BankingTab,             meta: { title: 'Banking', requiresBma: true } },
      { path: 'reporting',     name: 'AdminReporting',     component: FinancialAnalyticsTab,  meta: { title: 'Customer Spending' } },
      { path: 'site-analytics', name: 'AdminSiteAnalytics', component: SiteAnalyticsView,     meta: { title: 'Site Analytics' } },
      { path: 'settings',      redirect: '/admin/settings/business-profile' },
      { path: 'settings/rooms',   redirect: '/admin/settings/business-profile/rooms' },
      {
        path: 'settings/business-profile',
        name: 'AdminSettingsBusinessProfile',
        component: BusinessProfileView,
        children: [
          { path: '',        redirect: '/admin/settings/business-profile/profile' },
          { path: 'profile', name: 'AdminSettingsBusinessProfileProfile', component: BusinessProfileTab, meta: { title: 'Business Profile' } },
          { path: 'rooms',   name: 'AdminSettingsBusinessProfileRooms',   component: RoomsView,          meta: { title: 'Business Profile' } },
          // Moved to Billing & Payroll › Pricing — keep old links working.
          { path: 'lesson-packages',   redirect: '/admin/pricing/lesson-packages' },
          { path: 'membership-offers', redirect: '/admin/pricing/membership-offers' },
          { path: 'hardship-offer',    redirect: '/admin/pricing/hardship-offer' },
          { path: 'stripe', name: 'AdminSettingsBusinessProfileStripe', component: StripeConnectTab, meta: { title: 'Business Profile' } },
          { path: 'site-analytics', name: 'AdminSettingsBusinessProfileSiteAnalytics', component: SiteAnalyticsSettingsTab, meta: { title: 'Business Profile' } },
          { path: 'agreement', name: 'AdminSettingsBusinessProfileAgreement', component: AgreementPoliciesTab, meta: { title: 'Business Profile' } },
        ]
      },
      { path: 'settings/lesson-packages', redirect: '/admin/pricing/lesson-packages' },
      { path: 'settings/dns-setup', name: 'AdminSettingsDnsSetup', component: DnsSetupView, meta: { title: 'DNS & Email Setup', requiresBma: true } },
      { path: 'settings/widgets', name: 'AdminSettingsWidgets', component: WidgetsView, meta: { title: 'Settings' } },
      { path: 'settings/notifications', name: 'AdminSettingsNotifications', component: NotificationPreferencesView, meta: { title: 'Notification Preferences' } },
    ]
  },
  {
    path: '/my-schedule',
    name: 'MySchedule',
    component: App,
    // DayView/WeekView auto-scroll to the relevant time on load — generic
    // restore would fight that, same reasoning as the Dashboard route above.
    meta: { requiresAuth: true, skipScrollRestore: true }
  },
  {
    path: '/students',
    name: 'DashboardStudents',
    component: App,
    meta: { requiresAuth: true }
  },
  // Team Calendar now lives in the dashboard's calendar pane — keep old links working
  { path: '/team-calendar', redirect: '/' },
  // Teacher self-service availability editor (token-gated, no login required)
  {
    path: '/availability/:token',
    name: 'TeacherAvailability',
    component: TeacherAvailabilityPage,
    meta: { requiresAuth: false, faviconSurface: 'token' }
  },
  // Hardship membership offer acceptance page (token-gated, no login required)
  {
    path: '/hardship/:token',
    name: 'HardshipAccept',
    component: HardshipAcceptPage,
    meta: { requiresAuth: false, faviconSurface: 'token' }
  },
  // Self-service notification preferences page (token-gated, no login required)
  {
    path: '/preferences/:token',
    name: 'CommunicationPreferences',
    component: CommunicationPreferencesPage,
    meta: { requiresAuth: false, faviconSurface: 'token' }
  },
  // Catch-all redirect to login
  {
    path: '/:pathMatch(.*)*',
    redirect: '/login'
  }
]

// ── Scroll restore ───────────────────────────────────────────────────────────
// Recovers window scroll position across a page reload (e.g. iOS discarding a
// backgrounded tab) via sessionStorage, since that (unlike JS memory) survives
// the reload. Routes whose view already owns its own "scroll to the relevant
// content" logic opt out via meta.skipScrollRestore (see route defs above).
const SCROLL_KEY_PREFIX = 'scroll-pos:'
let scrollSaveTimer = null
const saveScrollPosition = () => {
  sessionStorage.setItem(SCROLL_KEY_PREFIX + router.currentRoute.value.fullPath, String(window.scrollY))
}
window.addEventListener('scroll', () => {
  clearTimeout(scrollSaveTimer)
  scrollSaveTimer = setTimeout(saveScrollPosition, 150)
}, { passive: true })
document.addEventListener('visibilitychange', () => {
  if (document.hidden) saveScrollPosition()
})

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.meta?.skipScrollRestore) return false
    const saved = sessionStorage.getItem(SCROLL_KEY_PREFIX + to.fullPath)
    if (saved != null) return { top: Number(saved) }
    if (savedPosition) return savedPosition
    return { top: 0 }
  }
})

// Recovers from stale lazy-loaded chunks: if a tab stays open across a deploy,
// its route components (each a separate content-hashed file — see the admin
// view imports above) point at filenames that no longer exist on the server,
// since a deploy fully replaces dist/ rather than keeping old chunks around.
// A dynamic import() for one then 404s and silently rejects with no built-in
// recovery, which looks like the whole app freezing — nav links stop doing
// anything. Force a real reload to the intended destination so the browser
// picks up the current chunk manifest instead of leaving the SPA stuck.
router.onError((error, to) => {
  if (/Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(error.message)) {
    window.location.href = to.fullPath
  }
})
window.addEventListener('vite:preloadError', () => window.location.reload())

router.beforeEach((to, from, next) => {
  const requiresAuth  = to.matched.some(record => record.meta.requiresAuth)
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin)
  // Permanently CSML/BMA-only routes (Operations, Onboarding Specialist,
  // Banking) — not a §10 feature toggle, see AdminNav.vue's "bmaOnly" items
  // and utils/tenant_utils.py's require_bma_only() on the backend, which is
  // the actual enforcement; this guard is UX only (2026-08-19).
  const requiresBma = to.matched.some(record => record.meta.requiresBma)
  const user = JSON.parse(localStorage.getItem('user') || 'null')
  const isAuthenticated = !!user
  // While Demo Mode previews someone, the workspace opens to whoever is
  // previewed, not to the real admin.
  const demoMode = useDemoMode()
  const permissions = demoMode.permissionsFor(user?.permissions)
  const isAdmin = permissions.length > 0
  // Views the user's sidebar hides still route here; AdminLayout shows
  // NoAccess.vue in their place.

  if (requiresAuth && !isAuthenticated) {
    // Carry the target through login so emailed deep links (e.g. staff
    // "Manage notification settings") land where they pointed, not on /.
    next(to.fullPath === '/' ? '/login' : { path: '/login', query: { redirect: to.fullPath } })
  } else if (requiresAdmin && !isAdmin) {
    next('/')
  } else if (requiresBma && user?.tenant_id !== 1) {
    next('/')
  } else if (to.path === '/login' && isAuthenticated) {
    next('/')
  } else {
    next()
  }
})

// On mobile, the browser/PWA reopens at whatever tab path was last visited (the
// URL persists across reloads). Force Attendance on the very first navigation of
// a session so mobile always boots there, without disturbing in-app tab switching
// afterward.
let isFirstNavigation = true
const mobileDashboardTabPaths = ['/stats', '/students', '/my-schedule', '/signups']
router.beforeEach((to, from, next) => {
  if (isFirstNavigation) {
    isFirstNavigation = false
    if (window.innerWidth < MOBILE_BREAKPOINT && mobileDashboardTabPaths.includes(to.path)) {
      return next('/')
    }
  }
  next()
})

// ── Per-surface favicon ──────────────────────────────────────────────────────
// One static index.html serves every route (see its own "Per-tenant branding"
// comment for why it can't be templated server-side), so swapping the icon per
// surface has to happen client-side by rewriting the <link rel="icon"> href.
// Falls back to the default /favicon.gif whenever a tenant hasn't set one for
// that surface. Reapplied both on navigation (surface can change) and whenever
// the branding fetch resolves after the current page already painted.
const DEFAULT_FAVICON = '/favicon.gif'
function faviconSurfaceFor(route) {
  if (route.path.startsWith('/admin')) return 'admin'
  return route.matched.slice().reverse().find(r => r.meta?.faviconSurface)?.meta?.faviconSurface || 'dashboard'
}
function applyFavicon(url) {
  let link = document.querySelector('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = url || DEFAULT_FAVICON
}
const branding = useTenantBranding()
let currentFaviconSurface = 'dashboard'
function faviconUrlForSurface(surface) {
  if (surface === 'admin') return branding.faviconAdminUrl.value
  if (surface === 'token') return branding.faviconTokenUrl.value
  return branding.faviconDashboardUrl.value
}
watch(
  [branding.faviconAdminUrl, branding.faviconTokenUrl, branding.faviconDashboardUrl],
  () => applyFavicon(faviconUrlForSurface(currentFaviconSurface))
)

router.afterEach((to) => {
  if (to.path.startsWith('/admin/')) {
    localStorage.setItem('adminLastView', to.path)
  }
  let title = to.matched.slice().reverse().find(r => r.meta?.title)?.meta?.title
  // Query-mode tabs (see adminNavItems.js) share one route — name the tab too.
  const tabLabel = to.query.tab && findNavView(to.path)?.view.tabs?.find(t => t.key === to.query.tab)?.label
  if (title && tabLabel) title = `${tabLabel} · ${title}`
  const { acronym } = useTenantBranding()
  document.title = title ? `${title} | ${acronym.value}` : acronym.value

  currentFaviconSurface = faviconSurfaceFor(to)
  applyFavicon(faviconUrlForSurface(currentFaviconSurface))
})

export default router
